import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl, InputLabel, IconButton,
  Tooltip, CircularProgress, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, TableSortLabel, Snackbar, Alert, InputAdornment, Grid, Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { getContacts, updateContact, deleteContact, importContacts } from '../api/whatsapp';
import client from '../api/client';

const statusChip = (status) => {
  const isSub = status === 'subscribed';
  return (
    <Chip label={isSub ? 'Subscribed' : 'Unsubscribed'} size="small"
      sx={{
        color: '#fff', fontWeight: 600, fontSize: 11, borderRadius: '6px',
        bgcolor: isSub ? '#22c55e' : '#ef4444',
      }} />
  );
};

const emptyContact = { fullName: '', email: '', country: '', birthDate: '', weddingDate: '' };

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [subscribed, setSubscribed] = useState(0);
  const [unsubscribed, setUnsubscribed] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tabIndex, setTabIndex] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [editContact, setEditContact] = useState(null);
  const [editForm, setEditForm] = useState(emptyContact);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: '', lastName: '', mobileNumber: '', email: '', birthDate: '', weddingDate: '' });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getContacts({
        page, limit, search,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy, sortOrder,
      });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setSubscribed(data.subscribed || 0);
      setUnsubscribed(data.unsubscribed || 0);
    } catch { setSnack({ open: true, message: 'Failed to load contacts', severity: 'error' }); }
    setLoading(false);
  }, [page, limit, search, statusFilter, sortBy, sortOrder]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleSort = (col) => {
    if (sortBy === col) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('asc'); }
  };

  const handleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    if (selected.length === contacts.length) setSelected([]);
    else setSelected(contacts.map(c => c._id));
  };

  const handleEditOpen = (contact) => {
    setEditContact(contact);
    setEditForm({
      fullName: contact.fullName || '',
      email: contact.email || '',
      country: contact.country || '',
      birthDate: contact.birthDate ? contact.birthDate.split('T')[0] : '',
      weddingDate: contact.weddingDate ? contact.weddingDate.split('T')[0] : '',
    });
  };

  const handleEditSave = async () => {
    if (!editContact) return;
    try {
      await updateContact(editContact._id, editForm);
      setSnack({ open: true, message: 'Contact updated', severity: 'success' });
      setEditContact(null);
      fetchContacts();
    } catch { setSnack({ open: true, message: 'Failed to update', severity: 'error' }); }
  };

  const toggleSubscription = async (contact) => {
    const newStatus = contact.subscriptionStatus === 'subscribed' ? 'unsubscribed' : 'subscribed';
    try {
      await updateContact(contact._id, { subscriptionStatus: newStatus });
      setSnack({ open: true, message: `Contact ${newStatus === 'subscribed' ? 'subscribed' : 'unsubscribed'}`, severity: 'success' });
      fetchContacts();
    } catch { setSnack({ open: true, message: 'Failed to update status', severity: 'error' }); }
  };

  const toggleOptIn = async (contact) => {
    const newOptIn = !contact.optIn;
    try {
      await client.put(`/api/whatsapp/contacts/${contact._id}/opt-in`, { optIn: newOptIn });
      setSnack({ open: true, message: newOptIn ? 'Contact opted in' : 'Contact opted out', severity: 'success' });
      fetchContacts();
    } catch { setSnack({ open: true, message: 'Failed to update opt-in', severity: 'error' }); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteContact(deleteId);
      setContacts(prev => prev.filter(c => c._id !== deleteId));
      setSnack({ open: true, message: 'Contact deleted', severity: 'success' });
    } catch { setSnack({ open: true, message: 'Failed to delete', severity: 'error' }); }
    setDeleting(false);
    setDeleteId(null);
  };

  const handleDownloadSample = () => {
    const headers = ['First Name', 'Last Name', 'Phone Number', 'Birth Date', 'Wedding Date', 'Email'];
    const sampleRows = [
      ['Gayani', 'Singh', '918860768823', '15-04-1990', '', 'gayani@example.com'],
      ['Rakesh', 'Mahobiya', '917587732007', '22-08-1988', '10-12-2020', 'rakesh@example.com'],
    ];
    const csv = [headers.join(','), ...sampleRows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sample-contacts.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importContacts(importFile);
      setImportResult(result);
      if (result.imported > 0) {
        setSnack({ open: true, message: `${result.imported} contacts imported successfully`, severity: 'success' });
        fetchContacts();
      } else {
        setSnack({ open: true, message: 'No contacts were imported', severity: 'warning' });
      }
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.message || 'Import failed', severity: 'error' });
    }
    setImporting(false);
  };

  const handleExport = () => {
    const headers = ['Full Name', 'Country', 'Mobile Number', 'Opt-In', 'Registration Date', 'Birth Date', 'Wedding Date', 'Email', 'Status'];
    const rows = contacts.map(c => [
      c.fullName, c.country, c.mobileNumber, c.optIn !== false ? 'Yes' : 'No',
      c.registrationDate ? new Date(c.registrationDate).toLocaleDateString() : '',
      c.birthDate ? new Date(c.birthDate).toLocaleDateString() : '',
      c.weddingDate ? new Date(c.weddingDate).toLocaleDateString() : '',
      c.email, c.subscriptionStatus,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v || ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'contacts.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / limit);
  const cols = [
    { id: 'fullName', label: 'Full Name' },
    { id: 'country', label: 'Country' },
    { id: 'mobileNumber', label: 'Mobile Number' },
    { id: 'optIn', label: 'Opt-In' },
    { id: 'registrationDate', label: 'Registration Date' },
    { id: 'birthDate', label: 'Birth Date' },
    { id: 'weddingDate', label: 'Wedding Date' },
    { id: 'email', label: 'Email' },
    { id: 'action', label: 'Action' },
  ];

  return (
    <Box sx={{ p: 4, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Contacts</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}
              sx={{ borderColor: '#075e54', color: '#075e54', borderRadius: 2 }}>
              Export Contacts
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}
              sx={{ borderRadius: 2 }}>
              Add Contact
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper className="glass-card" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}
            sx={{
              px: 2, pt: 0.5, minHeight: 44,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, color: '#64748b', minHeight: 44, py: 0 },
              '& .Mui-selected': { color: '#075e54 !important' },
              '& .MuiTabs-indicator': { bgcolor: '#075e54' },
            }}>
            <Tab label="All Contacts" />
            <Tab label="Groups" />
            <Tab label="Google Sheet" />
          </Tabs>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={2}>
          {[
            { label: 'Total Contacts', value: total, color: '#075e54', bg: 'rgba(7,94,84,0.1)' },
            { label: 'Subscribed Contacts', value: subscribed, color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
            { label: 'Unsubscribed Contacts', value: unsubscribed, color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
          ].map((stat, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Paper className="card-hover glass-card" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h3" fontWeight={800} color={stat.color} sx={{ lineHeight: 1, mb: 0.5 }}>
                  {stat.value.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="#64748b" fontWeight={600}>{stat.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Search & Filters */}
        <Paper className="glass-card" sx={{ p: 2.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name or phone..."
            size="small" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} label="Status">
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="subscribed">Subscribed</MenuItem>
              <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Show entries</InputLabel>
            <Select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} label="Show entries">
              <MenuItem value={10}>10 entries</MenuItem>
              <MenuItem value={20}>20 entries</MenuItem>
              <MenuItem value={50}>50 entries</MenuItem>
              <MenuItem value={100}>100 entries</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchContacts} size="small" sx={{ color: '#075e54' }}>
            <RefreshIcon />
          </IconButton>
        </Paper>

        {/* Table */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
        ) : contacts.length === 0 ? (
          <Paper className="glass-card" sx={{ textAlign: 'center', py: 8, borderRadius: 3 }}>
            <Typography variant="h6" color="#1e293b" fontWeight={700} gutterBottom>No contacts found</Typography>
            <Typography variant="body2" color="#64748b">Contacts will appear here when users message your WhatsApp number.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} className="glass-card" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={contacts.length > 0 && selected.length === contacts.length}
                      indeterminate={selected.length > 0 && selected.length < contacts.length}
                      onChange={handleSelectAll}
                      sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#075e54' } }}
                    />
                  </TableCell>
                  {cols.map(col => (
                    <TableCell key={col.id} sx={{ fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
                      {col.id === 'action' ? col.label : (
                        <TableSortLabel
                          active={sortBy === col.id}
                          direction={sortBy === col.id ? sortOrder : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map(contact => (
                  <TableRow key={contact._id} hover
                    selected={selected.includes(contact._id)}
                    sx={{ '&:hover': { bgcolor: 'rgba(7,94,84,0.04)' } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(contact._id)}
                        onChange={() => handleSelect(contact._id)}
                        sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#075e54' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600} fontSize={14} color="#1e293b">{contact.fullName}</Typography>
                        {statusChip(contact.subscriptionStatus)}
                      </Box>
                    </TableCell>
                    <TableCell>{contact.country || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize={13}>
                        {contact.mobileNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={contact.optIn !== false ? 'Opted In' : 'Not Opted In'}
                        size="small"
                        onClick={() => toggleOptIn(contact)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 600, fontSize: 11, borderRadius: '6px',
                          color: '#fff',
                          bgcolor: contact.optIn !== false ? '#22c55e' : '#94a3b8',
                          '&:hover': { bgcolor: contact.optIn !== false ? '#16a34a' : '#64748b' },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="#64748b">
                        {contact.registrationDate ? new Date(contact.registrationDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="#64748b">
                        {contact.birthDate ? new Date(contact.birthDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="#64748b">
                        {contact.weddingDate ? new Date(contact.weddingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize={13}>
                        {contact.email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit" arrow>
                          <IconButton size="small" onClick={() => handleEditOpen(contact)}
                            sx={{ color: '#64748b', '&:hover': { color: '#075e54' } }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={contact.subscriptionStatus === 'subscribed' ? 'Unsubscribe' : 'Subscribe'} arrow>
                          <IconButton size="small" onClick={() => toggleSubscription(contact)}
                            sx={{ color: contact.subscriptionStatus === 'subscribed' ? '#22c55e' : '#94a3b8' }}>
                            <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                              {contact.subscriptionStatus === 'subscribed' ? '✓' : '○'}
                            </Box>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton size="small" onClick={() => setDeleteId(contact._id)}
                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {total > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" color="#64748b">
              Showing {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)} of {total} entries
              {selected.length > 0 && ` — ${selected.length} selected`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage(prev => Math.max(1, prev - 1))}
                sx={{ color: '#64748b', minWidth: 36, borderRadius: 1 }}>‹</Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <Button key={p} size="small" onClick={() => setPage(p)}
                    sx={{
                      minWidth: 36, borderRadius: 1, fontWeight: 600, fontSize: 13,
                      bgcolor: p === page ? '#075e54' : 'transparent',
                      color: p === page ? '#fff' : '#64748b',
                      '&:hover': p !== page ? { bgcolor: 'rgba(7,94,84,0.08)' } : {},
                    }}>
                    {p}
                  </Button>
                );
              })}
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                sx={{ color: '#64748b', minWidth: 36, borderRadius: 1 }}>›</Button>
            </Box>
          </Box>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editContact} onClose={() => setEditContact(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Edit Contact</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Full Name" fullWidth size="small" value={editForm.fullName}
                onChange={e => setEditForm({ ...editForm, fullName: e.target.value })} />
              <TextField label="Email" fullWidth size="small" value={editForm.email}
                onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
              <TextField label="Country" fullWidth size="small" value={editForm.country}
                onChange={e => setEditForm({ ...editForm, country: e.target.value })} />
              <TextField label="Birth Date" type="date" fullWidth size="small" value={editForm.birthDate}
                onChange={e => setEditForm({ ...editForm, birthDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Wedding Date" type="date" fullWidth size="small" value={editForm.weddingDate}
                onChange={e => setEditForm({ ...editForm, weddingDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setEditContact(null)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={handleEditSave} variant="contained" sx={{ borderRadius: 2 }}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)}
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>Delete Contact</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="#64748b">Are you sure? This will also delete the conversation history.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setDeleteId(null)} disabled={deleting} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} variant="contained" color="error" sx={{ borderRadius: 2 }}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Contact Dialog */}
        <Dialog open={addDialog} onClose={() => { setAddDialog(false); setImportFile(null); setImportResult(null); setAddForm({ firstName: '', lastName: '', mobileNumber: '', email: '', birthDate: '', weddingDate: '' }); }}
          maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Contact</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {/* File Import Section */}
            <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: 'rgba(7,94,84,0.04)', border: '1px dashed rgba(7,94,84,0.25)', mb: 3 }}>
              <Typography fontWeight={700} fontSize={14} color="#075e54" mb={1}>File Import</Typography>
              <Button size="small" variant="text" startIcon={<DownloadIcon />} onClick={handleDownloadSample}
                sx={{ textTransform: 'none', color: '#075e54', fontWeight: 600, fontSize: 12, mb: 1.5 }}>
                Download sample file
              </Button>

              <Box
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={e => {
                  e.preventDefault(); e.stopPropagation();
                  const f = e.dataTransfer.files[0];
                  if (f) setImportFile(f);
                }}
                sx={{
                  border: '2px dashed', borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                  borderColor: importFile ? '#075e54' : '#cbd5e1',
                  bgcolor: importFile ? 'rgba(7,94,84,0.06)' : 'rgba(255,255,255,0.6)',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#075e54', bgcolor: 'rgba(7,94,84,0.06)' },
                }}
                onClick={() => document.getElementById('import-file-input').click()}
              >
                <input type="file" id="import-file-input" hidden accept=".xls,.xlsx,.csv"
                  onChange={e => { if (e.target.files[0]) setImportFile(e.target.files[0]); }} />
                {importFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <FileIcon sx={{ fontSize: 28, color: '#075e54' }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography fontWeight={600} fontSize={13} color="#1e293b">{importFile.name}</Typography>
                      <Typography variant="caption" color="#64748b">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                      sx={{ ml: 'auto', color: '#ef4444', minWidth: 'auto', textTransform: 'none', fontSize: 12 }}>
                      Remove
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 36, color: '#94a3b8', mb: 1 }} />
                    <Typography variant="body2" color="#64748b" fontWeight={500}>
                      Click or drag file to upload
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="#94a3b8" display="block" mt={1} textAlign="center">
                Upload only .xls, .xlsx, or .csv files, up to 10,000 contacts, and less than 5 MB in size.
              </Typography>

              {importFile && !importResult && (
                <Button variant="contained" fullWidth startIcon={<CloudUploadIcon />} onClick={handleFileImport}
                  disabled={importing} sx={{ mt: 2, borderRadius: 2 }}>
                  {importing ? 'Importing...' : 'Import File'}
                </Button>
              )}

              {importResult && (
                <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: importResult.imported > 0 ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)' }}>
                  <Typography variant="body2" fontWeight={600} color={importResult.imported > 0 ? '#166534' : '#92400e'}>
                    {importResult.imported > 0 ? `✓ ${importResult.imported} contacts imported` : 'No contacts imported'}
                  </Typography>
                  {importResult.skipped > 0 && (
                    <Typography variant="caption" color="#a16207">
                      {importResult.skipped} skipped
                      {importResult.skippedDetails?.length > 0 && (
                        <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                          {importResult.skippedDetails.map((d, i) => (
                            <li key={i}><Typography variant="caption" color="#a16207">{d.row}: {d.reason}</Typography></li>
                          ))}
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>

            {/* OR Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="body2" color="#94a3b8" fontWeight={600} fontSize={12}>OR</Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Manual Form */}
            <Typography variant="body2" color="#64748b" fontWeight={600} mb={1.5}>Add manually</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="First Name *" fullWidth size="small" value={addForm.firstName}
                  onChange={e => setAddForm({ ...addForm, firstName: e.target.value })} />
                <TextField label="Last Name" fullWidth size="small" value={addForm.lastName}
                  onChange={e => setAddForm({ ...addForm, lastName: e.target.value })} />
              </Box>
              <TextField label="Phone Number (With Country Code) *" fullWidth size="small" value={addForm.mobileNumber}
                onChange={e => setAddForm({ ...addForm, mobileNumber: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="9198xxxxxxxx" />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Birth Date" fullWidth size="small" value={addForm.birthDate}
                  onChange={e => setAddForm({ ...addForm, birthDate: e.target.value })}
                  placeholder="dd-mm-yyyy" />
                <TextField label="Wedding Date" fullWidth size="small" value={addForm.weddingDate}
                  onChange={e => setAddForm({ ...addForm, weddingDate: e.target.value })}
                  placeholder="dd-mm-yyyy" />
              </Box>
              <TextField label="Email Address" fullWidth size="small" value={addForm.email}
                onChange={e => setAddForm({ ...addForm, email: e.target.value })} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => { setAddDialog(false); setImportFile(null); setImportResult(null); setAddForm({ firstName: '', lastName: '', mobileNumber: '', email: '', birthDate: '', weddingDate: '' }); }}
              sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={async () => {
              if (!addForm.firstName || !addForm.mobileNumber) {
                setSnack({ open: true, message: 'First Name and Phone Number are required', severity: 'error' });
                return;
              }
              try {
                const client = (await import('../api/client')).default;
                await client.post('/api/whatsapp/contacts', {
                  fullName: (addForm.firstName + ' ' + addForm.lastName).trim(),
                  mobileNumber: addForm.mobileNumber,
                  email: addForm.email,
                  birthDate: addForm.birthDate,
                  weddingDate: addForm.weddingDate,
                });
                setSnack({ open: true, message: 'Contact added', severity: 'success' });
                setAddDialog(false);
                setImportFile(null);
                setImportResult(null);
                setAddForm({ firstName: '', lastName: '', mobileNumber: '', email: '', birthDate: '', weddingDate: '' });
                fetchContacts();
              } catch (e) {
                setSnack({ open: true, message: e.response?.data?.message || 'Failed to add contact', severity: 'error' });
              }
            }} variant="contained" sx={{ borderRadius: 2 }}>Add Contact</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity}
            sx={{ width: '100%', borderRadius: 2 }}>{snack.message}</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Contacts;
